import { graphql, type FragmentOf } from "~/lib/dato-graphql";

// Core fragments migrated from .gql files to gql.tada
// Using @_unmask for easier migration from codegen
export const responsiveImageFragment = graphql(`
  fragment ResponsiveImageFragment on ResponsiveImage @_unmask {
    __typename
    srcSet
    webpSrcSet
    sizes
    src
    width
    height
    aspectRatio
    alt
    title
    base64
    bgColor
  }
`);

export const tourPropsFragment = graphql(`
  fragment TourPropsFragment on TourRecord @_unmask {
    __typename
    id
    title
    slug
    summary
    fromPrice
    perPerson
    duration
    tripadvisorReviewCount
    tripadvisorRating
  }
`);

export const articlePropsFragment = graphql(
  `
    fragment ArticleCardFragment on ArticleRecord @_unmask {
      __typename
      id
      title
      slug
      date
      _publishedAt
      thumbnail {
        alt
        title
        responsiveImage(
          imgixParams: { fit: crop, w: 500, h: 500, auto: format }
        ) {
          ...ResponsiveImageFragment
        }
      }
      _status
    }
  `,
  [responsiveImageFragment],
);

export const tripPropsFragment = graphql(`
  fragment TripCardFragment on TripRecord @_unmask {
    __typename
    id
    title
    slug
    tripDestinations {
      id
      location {
        id
        name
        slug
        geolocation {
          latitude
          longitude
        }
      }
      tripDays {
        id
      }
    }
  }
`);

export const guidePropsFragment = graphql(
  `
    fragment GuideCardFragment on GuideRecord @_unmask {
      __typename
      id
      title
      slug
      summary
      thumbnail {
        alt
        title
        responsiveImage(
          imgixParams: { fit: crop, w: 500, h: 500, auto: format }
        ) {
          ...ResponsiveImageFragment
        }
      }
    }
  `,
  [responsiveImageFragment],
);

export const placePropsFragment = graphql(
  `
    fragment PlacePropsFragment on PlaceRecord @_unmask {
      __typename
      id
      title
      slug
      heroImage {
        alt
        title
        responsiveImage(
          imgixParams: { fit: crop, w: 500, h: 500, auto: format }
        ) {
          ...ResponsiveImageFragment
        }
      }
    }
  `,
  [responsiveImageFragment],
);

export const featuredArticleFragment = graphql(
  `
    fragment FeaturedArticleFragment on ArticleRecord @_unmask {
      __typename
      id
      title
      slug
      hero {
        alt
        responsiveImage(
          imgixParams: { fit: crop, w: 540, h: 420, auto: format }
        ) {
          ...ResponsiveImageFragment
        }
      }
      _status
      _firstPublishedAt
    }
  `,
  [responsiveImageFragment],
);

export const seoMetaTagsFragment = graphql(`
  fragment SeoMetaTagsFragment on Tag @_unmask {
    __typename
    attributes
    content
    tag
  }
`);

export const adminUserFragment = graphql(
  `
    fragment AdminUserFragment on ContributorRecord @_unmask {
      __typename
      name
      emailAddress
      profilePicture {
        responsiveImage(
          imgixParams: {
            auto: format
            fit: facearea
            facepad: 10
            w: 45
            h: 45
          }
        ) {
          ...ResponsiveImageFragment
        }
      }
    }
  `,
  [responsiveImageFragment],
);

// Fragment type helpers for components
export type ArticleProps = FragmentOf<typeof articlePropsFragment>;
export type TourProps = FragmentOf<typeof tourPropsFragment>;
export type TripProps = FragmentOf<typeof tripPropsFragment>;
export type PlaceProps = FragmentOf<typeof placePropsFragment>;
export type ResponsiveImageProps = FragmentOf<typeof responsiveImageFragment>;
export type AdminUserProps = FragmentOf<typeof adminUserFragment>;
